import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'

const BRAND        = '#6eb446'
const BRAND_DARK   = '#2d5918'
const BRAND_SHADOW = '0 8px 24px rgba(110,180,70,0.35)'

interface Props {
  pos_date        : string
  pos_maintenance : boolean
}

const MaintenancePage: React.FC<Props> = ({ pos_date }) => {
  const { t, i18n } = useTranslation()

  const [now, setNow] = useState(DateTime.now({ zone: 'utc' }))

  useEffect(() => {
    document.body.classList.add('page-home')
    return () => document.body.classList.remove('page-home')
  }, [])

  useEffect(() => {
    const id = setInterval(() => setNow(prev => prev.plus({ seconds: 1 })), 1000)
    return () => clearInterval(id)
  }, [])

  const endTime = DateTime.fromJSDate(new Date(pos_date), { zone: 'utc' })
    .setLocale(i18n.language)
    .toFormat('yyyy LLLL dd – HH:mm:ss')

  return (
    <div style={{
      minHeight       : '100vh',
      backgroundColor : '#f8faf6',
      display         : 'flex',
      flexDirection   : 'column',
      alignItems      : 'center',
      justifyContent  : 'center',
      padding         : '2rem 1.5rem',
      fontFamily      : 'Nunito, sans-serif',
    }}>
      <style>{`
        @keyframes pulse-ring {
          0%   { transform: scale(0.94); box-shadow: 0 0 0 0 rgba(110,180,70,0.5); }
          70%  { transform: scale(1);    box-shadow: 0 0 0 18px rgba(110,180,70,0); }
          100% { transform: scale(0.94); box-shadow: 0 0 0 0 rgba(110,180,70,0); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .mp-gear { display: inline-block; animation: spin-slow 6s linear infinite; }
        .mp-gear-rev { display: inline-block; animation: spin-slow 4s linear infinite reverse; }
      `}</style>

      {/* Logo */}
      <div style={{ marginBottom: '2.5rem', animation: 'fadeUp 0.5s ease both' }}>
        <img
          src="https://labeldictate.com/assets/img/Label-Dictate-Logo-V2021.png"
          width={64}
          alt="Label Dictate"
        />
      </div>

      {/* Animated icon */}
      <div style={{
        marginBottom    : '1.5rem',
        animation       : 'fadeUp 0.5s ease 0.05s both',
        display         : 'flex',
        alignItems      : 'center',
        justifyContent  : 'center',
        gap             : '0.4rem',
      }}>
        <span className="mp-gear"      style={{ fontSize: 'clamp(2.5rem, 8vw, 3.5rem)', lineHeight: 1 }}>⚙️</span>
        <span className="mp-gear-rev"  style={{ fontSize: 'clamp(1.6rem, 5vw, 2.2rem)', lineHeight: 1 }}>⚙️</span>
        <span className="mp-gear"      style={{ fontSize: 'clamp(1rem, 3vw, 1.4rem)',   lineHeight: 1 }}>⚙️</span>
      </div>

      {/* Card */}
      <div style={{
        width           : '100%',
        maxWidth        : 500,
        backgroundColor : '#fff',
        borderRadius    : 24,
        boxShadow       : '0 8px 40px rgba(0,0,0,0.08)',
        border          : '1px solid #f1f5f9',
        overflow        : 'hidden',
        animation       : 'fadeUp 0.6s ease 0.1s both',
      }}>
        {/* gradient accent bar */}
        <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />

        <div style={{ padding: '2rem 2rem 2.25rem', textAlign: 'center' }}>

          {/* Pulse dot */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem' }}>
            <span style={{
              width           : 14,
              height          : 14,
              borderRadius    : '50%',
              backgroundColor : BRAND,
              animation       : 'pulse-ring 2s ease-out infinite',
              display         : 'inline-block',
            }} />
          </div>

          <h1 style={{
            margin       : '0 0 0.75rem',
            fontSize     : '1.35rem',
            fontWeight   : 800,
            color        : '#1e293b',
            letterSpacing: '-0.01em',
            lineHeight   : 1.3,
          }}>
            {t('maintenance.h1')}
          </h1>

          <p style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: '#64748b', lineHeight: 1.7 }}>
            {t('maintenance.p1')}
          </p>

          <p style={{ margin: '0 0 1.5rem', fontSize: '0.9rem', color: '#64748b', lineHeight: 1.7 }}>
            {t('maintenance.p2')}{' '}
            <a
              href="mailto:contact@labeldictate.com"
              style={{ color: BRAND, fontWeight: 700, textDecoration: 'none' }}
            >
              contact@labeldictate.com
            </a>
          </p>

          {/* ETA box */}
          <div style={{
            backgroundColor : '#f4fbf0',
            border          : `1px solid rgba(110,180,70,0.3)`,
            borderRadius    : 14,
            padding         : '1rem 1.25rem',
            marginBottom    : '1.5rem',
          }}>
            <p style={{ margin: '0 0 0.3rem', fontSize: '0.72rem', fontWeight: 700, color: BRAND_DARK, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {t('maintenance.hour')}
            </p>
            <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b', fontVariantNumeric: 'tabular-nums' }}>
              {endTime} <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8' }}>UTC</span>
            </p>
          </div>

          {/* Live clock */}
          <p style={{ margin: 0, fontSize: '0.72rem', color: '#cbd5e1', fontVariantNumeric: 'tabular-nums' }}>
            {now.setLocale(i18n.language).toFormat('HH:mm:ss')} UTC
          </p>

        </div>
      </div>

      {/* Footer */}
      <p style={{
        marginTop  : '2.5rem',
        fontSize   : '0.75rem',
        color      : '#cbd5e1',
        animation  : 'fadeUp 0.6s ease 0.2s both',
      }}>
        Label Dictate © {new Date().getFullYear()}
      </p>
    </div>
  )
}

export default MaintenancePage
